import { useQuery } from '../models/reactUtils'
import { observer } from 'mobx-react'
import React, { useEffect, useState } from 'react'

import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'

import { AppointmentButton } from './AppointmentButton'
import DetailIcon from './DetailIcon'
import OfficeHours from './OfficeHours'

import { matchedProviderLocations } from '../utils/providerLocations'

import './ProviderDetailsPanel.css'

export const mobileAppointmentSx = {
  height: 44,
  minHeight: 44,
  my: 0,
}

export const canBookLocation = location =>
  location?.practice?.isClaimed &&
  ['dolphin', 'manual', 'schedule_connect', 'intelibly_direct'].includes(
    location.bookingExperience
  )

export const MobileResultItem = observer(({ provider, location, onSelect }) => {
  const { store } = useQuery()
  const firstLocation =
    location ||
    matchedProviderLocations(provider, store)[0] ||
    store.locationStore.searchMatchedLocations.find(item =>
      item.providers?.some(p => p?.id === provider?.id)
    )
  const name =
    provider?.displayName ||
    location?.displayName ||
    location?.name ||
    location?.practice?.name
  return (
    <article
      className={'mobile-result' + (provider ? ' mobile-provider-result' : '')}
      onClick={() => onSelect(provider || location)}
    >
      <button
        type="button"
        className="mobile-result-heading"
        onClick={event => {
          event.stopPropagation()
          onSelect(provider || location)
        }}
        aria-label={'View details for ' + name}
      >
        {provider && (
          <Avatar
            src={provider.photoUrl || undefined}
            sx={{ width: 64, height: 64, bgcolor: '#d9d9d9' }}
          >
            <span />
          </Avatar>
        )}
        <span className="mobile-stack">
          <strong className="mobile-name">{name}</strong>
          {provider?.specialtyList && <span>{provider.specialtyList}</span>}
          {provider && <span>{provider.languagesSpoken?.join('\u30fb')}</span>}
        </span>
      </button>
      {firstLocation && (
        <div className="details-row">
          {provider && <DetailIcon name="location" />}
          <div className="mobile-stack">
            {provider && (
              <strong className="details-location-name">
                {firstLocation.displayName || firstLocation.practice?.name}
              </strong>
            )}
            <span>{firstLocation.formattedAddress}</span>
            <span>{firstLocation.formattedPhoneNumber}</span>
            {!provider && <span>{firstLocation.formattedLanguagesSpoken}</span>}
            {!provider && !!firstLocation.providers?.length && (
              <div className="mobile-avatar-row">
                {firstLocation.providers.slice(0, 4).map(p => (
                  <Avatar
                    key={p.id}
                    src={p.photoUrl || undefined}
                    sx={{ width: 32, height: 32, bgcolor: '#d9d9d9' }}
                  >
                    <span />
                  </Avatar>
                ))}
              </div>
            )}
            {!provider &&
              firstLocation.partnerTags?.filter(Boolean).map(tag => (
                <span key={tag.id} title={tag.description}>
                  {tag.name}
                </span>
              ))}
          </div>
        </div>
      )}
      {canBookLocation(firstLocation) && (
        <div
          onClick={event => event.stopPropagation()}
          style={{ alignSelf: 'stretch' }}
        >
          <AppointmentButton
            location={firstLocation}
            provider={provider}
            sx={mobileAppointmentSx}
          />
        </div>
      )}
    </article>
  )
})

const MobileLocationDetails = observer(
  ({ location, onBack, onProviderSelect, onShowLocation }) => {
    const { store } = useQuery()
    const [loading, setLoading] = useState(!location.providers)
    const [error, setError] = useState(false)
    useEffect(() => {
      if (location.providers) return
      let active = true
      location
        .loadProviders()
        .catch(() => {
          if (active) setError(true)
        })
        .finally(() => {
          if (active) setLoading(false)
        })
      return () => {
        active = false
      }
    }, [location])
    useEffect(() => {
      const handleKey = event => {
        if (event.key === 'Escape') onBack()
      }
      document.addEventListener('keydown', handleKey)
      return () => document.removeEventListener('keydown', handleKey)
    }, [onBack])
    const name =
      location.displayName || location.name || location.practice?.name
    return (
      <section
        className="provider-details mobile-location-details"
        role="dialog"
        aria-label={name + ' location details'}
      >
        <div className="mobile-location-body">
          <IconButton
            aria-label="Back to results"
            title="Back"
            onClick={onBack}
            sx={{ alignSelf: 'flex-end', width: 44, height: 44, p: 0 }}
          >
            <DetailIcon name="back" />
          </IconButton>
          <h2>{name}</h2>
          <div className="details-row">
            <DetailIcon name="location" />
            <span>{location.formattedAddress}</span>
          </div>
          {location.phoneNumber && (
            <div className="details-row">
              <DetailIcon name="location" />
              <a href={'tel:' + location.phoneNumber}>
                {location.formattedPhoneNumber}
              </a>
            </div>
          )}
          {location.officeHoursSummary?.length > 0 && (
            <div className="details-row">
              <span className="details-icon" aria-hidden="true">
                <AccessTimeRoundedIcon sx={{ fontSize: 22 }} />
              </span>
              <OfficeHours location={location} />
            </div>
          )}
          {location.specialties && (
            <div className="details-row">
              <DetailIcon name="dentistry" />
              <span>{location.specialties}</span>
            </div>
          )}
          {location.languagesSpoken?.length > 0 && (
            <div className="details-row">
              <DetailIcon name="lang" />
              <span>{location.languagesSpoken.join('\u30fb')}</span>
            </div>
          )}
          {location.partnerTags?.length > 0 && (
            <div className="details-row">
              <DetailIcon name="network" />
              <div className="mobile-stack">
                {location.partnerTags.filter(Boolean).map(tag => (
                  <span key={tag.id} title={tag.description}>
                    {tag.name}
                  </span>
                ))}
              </div>
            </div>
          )}
          {canBookLocation(location) && (
            <AppointmentButton location={location} sx={mobileAppointmentSx} />
          )}
          <Button
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderRadius: '16px',
              fontFamily: 'inherit',
              alignSelf: 'flex-start',
            }}
            disabled={!location.lat || !location.lng}
            onClick={() => {
              store.locationStore.setFocusLocation(location, {
                openInfoWindow: false,
              })
              onShowLocation(location)
            }}
          >
            Show on map
          </Button>
        </div>
        {loading && <p role="status">Loading providers...</p>}
        {error && <p role="alert">Provider details could not be loaded.</p>}
        {location.providers?.filter(Boolean).map(provider => (
          <button
            type="button"
            className="mobile-provider-link"
            key={provider.id}
            onClick={() => onProviderSelect(provider)}
          >
            <Avatar
              src={provider.photoUrl || undefined}
              sx={{ width: 48, height: 48, bgcolor: '#d9d9d9' }}
            >
              <span />
            </Avatar>
            <span className="mobile-stack">
              <strong className="mobile-name">{provider.displayName}</strong>
              <span>{provider.specialtyList}</span>
            </span>
          </button>
        ))}
      </section>
    )
  }
)

export default MobileLocationDetails
